import {
  canonicalizeInviteCode,
  hasStandardInviteFormat,
  isCanonicalInviteCode,
  isCustomInviteCode,
  normalizeInviteCode,
} from './promotion-config'

/** Public application fields returned by the website invite endpoint. */
export interface PromotionInviteApp {
  appName: string
  icon: string
  versionName: string
  /** 版本说明(随包提交),保留但邀请页不展示。 */
  description: string
  /** 应用配置里的标语,邀请页展示文案(#33);后台改 slogan 页面即变,为空则用占位兜底。 */
  slogan: string
}

/** 邀请码类型：标准码 / 自定义码（需求 5006346，与服务端 inviteType 取值一致）。 */
export type InviteType = 'STANDARD' | 'CUSTOM'

/** 解析接口 data 的增量字段（需求 5006346），老后端不返回时均为 undefined。 */
interface ResolveInviteData {
  app: PromotionInviteApp
  inviteType?: string
  canonicalCode?: string
}

/** Standard Honeypot API envelope. */
interface ApiEnvelope<T> {
  code: number
  msg: string
  data?: T
}

/** Official website invite page states. */
export type OfficialPromotionState =
  | { kind: 'home' }
  | { kind: 'loading'; inviteCode: string }
  | { kind: 'ready'; inviteCode: string; app: PromotionInviteApp; inviteType: InviteType }
  // malformed:本地格式校验就没过(长度/字符集/校验位不对),这个码【从来没有效过】。
  // 必须与 invalid 分开:invalid 是后端 501009,含义是"这个码后端不认了",文案讲"不能用了"
  // 隐含它曾经有效;而用户手打错、链接被截断时看到"不能用了"会以为是我们过期了(#32)。
  | { kind: 'malformed' }
  | { kind: 'invalid' }
  | { kind: 'expired' }
  | { kind: 'unavailable' }
  // 自定义邀请码被后端拒绝（需求 5006346 新增错误码）。文案照 PRD §12 原样展示，
  // 官网不自己揣测别名为什么不能用。
  | { kind: 'rejected'; message: string }
  | { kind: 'error'; inviteCode: string }

/** Joins a public API path to the current environment API base URL. */
export function buildPublicApiUrl(path: string): string {
  const baseUrl = import.meta.env.VITE_HTTP_URL.replace(/\/+$/, '')
  return `${baseUrl}/${path.replace(/^\/+/, '')}`
}

export const ResolveInviteUrl = buildPublicApiUrl('/public/invite/resolve')

/**
 * 「在浏览器中打开」中转地址。
 * 微信内访问会返回 attachment 触发原生「用浏览器打开」；浏览器则 302 跳回官网。
 *
 * 原先这里要靠字符串替换从管理端基址推导出 App 端基址（改域名、改端口），
 * 因为三条能力分散在两个内部 tier 上。现在它们都由官网后端(honeypot-official)
 * 在同一个 /api/v1/public 下提供，这段推导不再需要。
 *
 * 注意本文件不要写出任何内部域名/端口：test 构建会产出 sourcemap，
 * 注释里的字面量同样会进交付产物。
 */
export function buildOpenInBrowserUrl(inviteCode: string): string {
  const query = inviteCode ? `?code=${encodeURIComponent(inviteCode)}` : ''
  return buildPublicApiUrl(`/public/official/open${query}`)
}

/** Removes all untrusted parameters while retaining one normalized invite code. */
export function sanitizeOfficialPromotionUrl(inviteCode: string): void {
  const url = new URL(window.location.href)
  url.search = ''
  url.hash = ''
  if (inviteCode) url.searchParams.set('code', inviteCode)
  window.history.replaceState(window.history.state, '', url)
}

// 「拉起失败回落」标记曾在这里维护（参数名 launchFallback）。
// 现已随拉起逻辑一并迁入 src/launch/honeypot-launch.js（参数名 hpLaunchFallback），
// 由 SDK 的 setup() 消费并通过 onNotInstalled 通知页面。
//
// ⚠️ 两套标记参数名不同，绝不能只迁一半：页面若还在读旧的 launchFallback，
// 浏览器带回来的却是 hpLaunchFallback，「未安装」这条路会静默失效。

/** Reads and validates the only supported website query parameter. */
/**
 * 旧版「拉起失败回落」标记（过渡期兼容，复审 #50 阻塞）。
 *
 * 这个参数不是分享出去的链接的一部分 —— 分享出去的只有 `?code=MG...`。
 * 它由页面在点击瞬间生成、交给浏览器作 `browser_fallback_url`，随即被 sanitize 清掉。
 *
 * 但确实存在一个真实窗口：**部署那一刻已经打开着旧页面、之后才点按钮的用户**。
 * 浏览器会带着旧参数把他送回新页面，新代码只认 hpLaunchFallback，于是不弹「未检测
 * 到蜜罐」，他得再点一次。窗口有限且可自愈，但兼容成本只有几行，值得做。
 *
 * 【放在官网而不是共用实现里】这是官网自己的历史约定，合作方页面从未产生过它。
 * 塞进共用实现等于让所有接入方去认一个与自己无关的参数。
 * 待线上不再出现该参数（一个发布周期后）即可删除本兼容。
 */
const LegacyLaunchFallbackParam = 'launchFallback'

export async function readOfficialInviteCode(): Promise<{
  code: string
  valid: boolean
  legacyLaunchFallback: boolean
}> {
  const params = new URLSearchParams(window.location.search)
  const raw = params.get('code') || ''
  // 需求 5006346：标准码判定优先（转大写 + 校验位，逻辑不变）；不像标准码再看是否合法
  // 自定义码（trim + 转小写）。两种都不是时保留原来的转大写，地址栏表现与改动前一致。
  const code = canonicalizeInviteCode(raw) || normalizeInviteCode(raw)
  const legacyLaunchFallback = params.get(LegacyLaunchFallbackParam) === '1'
  // 必须在 sanitize 之前读——sanitize 会把 code 以外的参数全部清掉。
  sanitizeOfficialPromotionUrl(code)
  if (!code) return { code: '', valid: true, legacyLaunchFallback }
  if (hasStandardInviteFormat(code)) {
    return { code, valid: await isInviteCodeAcceptable(code), legacyLaunchFallback }
  }
  // 自定义码只过本地格式与保留词；存不存在、启没启用一律交给后端，官网不自行判断。
  return { code, valid: isCustomInviteCode(code), legacyLaunchFallback }
}

/**
 * 本地校验邀请码，但**绝不因为校验本身出错而丢掉邀请码**（#13 / #16 / #19）。
 *
 * 本地校验只是省一次无谓请求的优化，不是安全边界——真正的裁决在后端 `invite/resolve`，
 * 非法码会返回 501009 并渲染成「这个链接不能用了」。可它一旦**抛异常**，调用方整条
 * `initialize()` 都会 reject，页面落到 `{ kind: 'home' }`，用户看到的是蜜罐首页，
 * 和「链接压根没带码」完全无法区分，排障时也毫无线索（生产已因此误报三轮）。
 *
 * 校验依赖 `hash-wasm`（需要 WebAssembly）与若干新 API，在老内核/受限 WebView 上都可能失败。
 * 所以这里把异常兜住并**放行**：宁可多发一次请求让后端裁决，也不能把有码的用户当成没码。
 */
async function isInviteCodeAcceptable(code: string): Promise<boolean> {
  try {
    return await isCanonicalInviteCode(code)
  } catch {
    return true
  }
}

/**
 * 自定义邀请码相关的后端拒绝码 → 页面文案（PRD §12 / 跨端契约 §2）。
 * 501026（限流）不在这里：它是临时失败，走 error 态保留「再试一次」。
 */
const CustomInviteRejections: Record<number, string> = {
  501018: '邀请码格式不正确',
  501019: '该邀请码不可使用',
  501021: '邀请码不存在或已失效',
  501022: '该邀请码已停用',
  501023: '该邀请码已失效',
  501024: '该邀请码当前未作为应用入口启用',
}

/**
 * 页面展示与拉起用的邀请码：后端给了 canonicalCode 且它本身就是规范值时以后端为准，
 * 否则沿用本地规范化结果（老后端不返回该字段）。
 */
function pickCanonicalCode(localCode: string, serverCode: unknown): string {
  if (typeof serverCode !== 'string' || !serverCode) return localCode
  return canonicalizeInviteCode(serverCode) === serverCode ? serverCode : localCode
}

/** Resolves public display data without accepting target APK metadata. */
export async function resolvePublicInvite(inviteCode: string): Promise<OfficialPromotionState> {
  try {
    const response = await fetch(ResolveInviteUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: inviteCode }),
    })
    const envelope = (await response.json()) as ApiEnvelope<ResolveInviteData>
    if (envelope.code === 501009) return { kind: 'invalid' }
    if (envelope.code === 501010) return { kind: 'expired' }
    if (envelope.code === 501011) return { kind: 'unavailable' }
    const rejection = CustomInviteRejections[envelope.code]
    if (rejection) return { kind: 'rejected', message: rejection }
    if (!response.ok || envelope.code === 429 || envelope.code === 503 || envelope.code !== 200 || !envelope.data?.app) {
      return { kind: 'error', inviteCode }
    }
    const code = pickCanonicalCode(inviteCode, envelope.data.canonicalCode)
    // inviteType 以后端为准；老后端不返回时按本地格式推断。
    const inviteType: InviteType = envelope.data.inviteType === 'CUSTOM' || envelope.data.inviteType === 'STANDARD'
      ? envelope.data.inviteType
      : (isCustomInviteCode(code) ? 'CUSTOM' : 'STANDARD')
    return { kind: 'ready', inviteCode: code, app: envelope.data.app, inviteType }
  } catch {
    return { kind: 'error', inviteCode }
  }
}

/** Constructs the existing Honeypot invite-install deep link. */
